import { StateError } from '@/components/app/state-error';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useLead } from '@/features/leads/api/get-lead/get-lead.hook';
import {
  CalendarDaysIcon,
  ClipboardCheckIcon,
  LockKeyholeIcon,
} from 'lucide-react';
import {
  AnswerValue,
  QuestionLabel,
  formatSubmittedAt,
} from './lead-form-parts';
import { getLeadFormResponse } from './lead-form-response';

/** Same contained reading experience on desktop and mobile; the profile stays underneath. */
export function LeadFormResponseDialog({
  leadId,
  onClose,
}: { leadId: string; onClose: () => void }) {
  const { lead, isLoading, isError } = useLead({ leadId });
  const name = lead
    ? `${lead.firstName} ${lead.lastName ?? ''}`.trim()
    : 'Customer';
  const response = lead ? getLeadFormResponse(lead) : null;
  const submittedAt = formatSubmittedAt(
    response?.submittedAt ?? null,
    "d MMM yyyy 'at' HH:mm"
  );
  const answerCount =
    response?.answers.filter((a) => a.status === 'answered').length ?? 0;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] max-w-[calc(100%-1rem)] flex-col gap-0 overflow-hidden rounded-[24px] border-border/60 p-0 shadow-2xl sm:max-w-[720px]"
        overlayClassName="bg-black/40 backdrop-blur-sm"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          requestAnimationFrame(() =>
            document
              .querySelector<HTMLElement>('[data-lead-form-trigger]')
              ?.focus()
          );
        }}
      >
        <DialogHeader className="shrink-0 gap-4 border-b bg-gradient-to-br from-primary/[0.05] via-background to-background px-5 pt-6 pb-5 text-left sm:px-8 sm:pt-7 sm:pb-6">
          <div className="flex items-center gap-2 pr-8 text-primary">
            <ClipboardCheckIcon className="size-4" />
            <span className="font-semibold text-[10px] uppercase tracking-[0.16em]">
              Lead form response
            </span>
          </div>
          <div className="space-y-2">
            <DialogTitle className="break-words pr-6 font-semibold text-[22px] leading-tight tracking-tight sm:text-2xl">
              <span className="sr-only">Lead form: </span>
              {isLoading
                ? 'Loading response…'
                : isError
                  ? 'Response unavailable'
                  : response
                    ? (response.formName ?? 'Form name not available')
                    : 'No lead form submitted'}
            </DialogTitle>
            <DialogDescription className="break-words text-sm">
              {name}
            </DialogDescription>
          </div>
          {response && !isLoading && !isError ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-muted-foreground text-xs">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDaysIcon className="size-3.5 shrink-0" />
                {submittedAt
                  ? `Submitted ${submittedAt}`
                  : 'Submission time not available'}
              </span>
              <span className="inline-flex rounded-full border bg-background/80 px-2.5 py-1 font-medium text-foreground">
                {answerCount} {answerCount === 1 ? 'answer' : 'answers'}
              </span>
            </div>
          ) : null}
        </DialogHeader>
        <div
          className="min-h-0 overflow-y-auto overscroll-contain"
          data-form-scroll=""
        >
          <LeadFormResponse leadId={leadId} />
        </div>
        <DialogFooter className="shrink-0 flex-row items-center justify-between gap-3 border-t bg-muted/20 px-5 py-4 sm:justify-between sm:px-8">
          <span className="inline-flex items-center gap-1.5 text-muted-foreground text-xs">
            <LockKeyholeIcon className="size-3" />
            Read-only response
          </span>
          <DialogClose asChild>
            <Button className="min-w-20 rounded-full">Done</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Saved answers inside the response dialog. */
export function LeadFormResponse({ leadId }: { leadId: string }) {
  const { lead, isLoading, isError, refetch } = useLead({ leadId });
  if (isLoading)
    return (
      <div
        role="status"
        aria-label="Loading form response"
        className="m-5 space-y-3 rounded-xl border bg-background p-5 sm:mx-8"
      >
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  if (isError)
    return (
      <div className="px-5 py-6 sm:px-8">
        <StateError
          message="Failed to load the form answers. Please try again."
          onRetry={() => refetch()}
        />
      </div>
    );

  const response = lead ? getLeadFormResponse(lead) : null;
  if (!response)
    return (
      <p className="m-5 rounded-xl border bg-background p-5 text-muted-foreground text-sm sm:mx-8">
        This customer has not submitted a lead form.
      </p>
    );
  const title = response.formName ?? 'Form name not available';

  return (
    <section aria-label={title} className="min-w-0 bg-background">
      <p className="border-b border-dashed px-5 py-4 text-muted-foreground text-xs leading-relaxed sm:px-8">
        Saved answers with the available form questions. Contact details reflect
        the current customer record.
      </p>
      {response.answers.length > 0 ? (
        <dl className="divide-y divide-border/60 px-5 sm:px-8">
          {response.answers.map((answer) => (
            <div
              key={answer.key}
              className="grid gap-2 py-5 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-8"
            >
              <dt className="whitespace-pre-wrap break-words text-muted-foreground text-[13px] leading-relaxed">
                <QuestionLabel answer={answer} />
              </dt>
              <dd className="min-w-0 text-[15px] leading-relaxed">
                <AnswerValue answer={answer} />
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="px-5 py-4 text-muted-foreground text-sm">
          No form answers on record. Contact details are available on the
          customer profile.
        </p>
      )}
      {response.adId || response.campaignId ? (
        <dl className="space-y-1 border-t bg-muted/20 px-5 py-4 text-muted-foreground text-[11px] sm:px-8">
          {response.adId ? (
            <div className="break-words">
              <dt className="inline">Ad ID: </dt>
              <dd className="inline">{response.adId}</dd>
            </div>
          ) : null}
          {response.campaignId ? (
            <div className="break-words">
              <dt className="inline">Campaign ID: </dt>
              <dd className="inline">{response.campaignId}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </section>
  );
}
